import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { LanguageService } from '../../core/services/language.service';
import { environment } from '../../../environments/environment';
import { ancHome, ancPath } from './anc-links';
import { AncStatusService } from './anc-status.service';
import { UniverseLink, injectUniverses } from './anc-universes';

/**
 * Footer of the art direction: promise, contacts, one column per universe and
 * the giant "ancelade" signature. Contacts are those of the current site.
 */
@Component({
  selector: 'app-anc-footer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, UpperCasePipe, TranslocoDirective],
  templateUrl: './anc-footer.component.html',
  styleUrl: './anc-footer.component.css',
})
export class AncFooterComponent {
  private readonly language = inject(LanguageService);
  private readonly universes = injectUniverses();
  /** Null until the public status API answers. */
  protected readonly operational = inject(AncStatusService).operational;
  protected readonly lang = computed(() => this.language.currentLang());

  protected readonly panelUrl = environment.panelUrl;
  protected readonly year = new Date().getFullYear();
  protected readonly columns = computed(() =>
    this.universes().map((universe) => ({
      key: universe.key,
      name: universe.name,
      links: universe.items.map((item) => ({ label: item.title, link: item.link })),
    }))
  );

  /** Language offered next to the current one: English, or French from English. */
  protected readonly otherLang = computed(() => (this.lang() === 'en' ? 'fr' : 'en'));
  protected readonly otherHome = computed(() => ancHome(this.otherLang()));
  protected readonly otherLabel = computed(
    () => this.language.languages.find((option) => option.code === this.otherLang())?.label ?? this.otherLang()
  );

  protected href(link: UniverseLink | string): string {
    return ancPath(this.lang(), typeof link === 'string' ? link : link.path);
  }

  protected home(): string {
    return ancHome(this.lang());
  }

  /** Stays on the current page in the other language; the link remains for crawlers and new tabs. */
  protected switchLang(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.language.switchLang(this.otherLang());
  }
}
